import {
  type APIEmoji,
  type APISticker,
  can,
  emojiUrl,
  PermissionFlags,
  stickerUrl,
} from '@alternativamais/chat-core';
import { useEffect, useMemo, useRef, useState } from 'react';

import { gravarPreferencia, lerPreferencia } from '../../armazenamento/local';
import { useAjusteDaConta } from '../../chat/ganchos';
import { useCliente, useEstado } from '../../chat/contexto';
import { Icone } from '../../componentes/Icone';
import { ARQUIVOS } from '../../config';
import {
  buscarEmojis,
  carregarEmojis,
  type EmojiUnicode,
  GRUPOS_EMOJI,
} from '../../utilidades/emojis';
import { noTamanho } from '../../componentes/imagens';

/** O que o seletor devolve: emoji Unicode ou do servidor. */
export type EmojiEscolhido = {
  id: string | null;
  name: string;
  animated?: boolean;
};

const RECENTES = 'emojis-recentes';
const MAX_RECENTES = 18;

function chaveDe(emoji: EmojiEscolhido) {
  return emoji.id ? `${emoji.name}:${emoji.id}` : emoji.name;
}

export function lerRecentes(): EmojiEscolhido[] {
  return lerPreferencia<EmojiEscolhido[]>(RECENTES, []);
}

function guardarRecente(emoji: EmojiEscolhido) {
  const lista = lerRecentes().filter(
    (item) => chaveDe(item) !== chaveDe(emoji),
  );
  gravarPreferencia(RECENTES, [emoji, ...lista].slice(0, MAX_RECENTES));
}

type Item = {
  emoji: EmojiEscolhido;
  rotulo: string;
  grupo: string;
};

/**
 * Seletor de emoji (C5): busca, usados com frequência, os do servidor e os
 * Unicode por categoria. Os Unicode carregam só quando abre.
 */
export function SeletorEmoji({
  canalId,
  aoEscolher,
  aoEscolherFigurinha,
}: {
  canalId: string;
  aoEscolher: (emoji: EmojiEscolhido) => void;
  /** No compositor: a aba Figurinhas (escolher envia na hora). */
  aoEscolherFigurinha?: (figurinha: APISticker) => void;
}) {
  const [aba, setAba] = useState<'emoji' | 'figurinhas'>('emoji');
  return (
    <div className="flex h-[440px] w-[400px] flex-col overflow-hidden rounded-xl border border-borda bg-elevado shadow-[0_12px_32px_rgba(0,0,0,.45)]">
      {aoEscolherFigurinha && (
        <div
          role="tablist"
          aria-label="Tipo de conteúdo"
          className="flex shrink-0 gap-1 px-3 pt-3"
        >
          {(['emoji', 'figurinhas'] as const).map((valor) => (
            <button
              key={valor}
              type="button"
              role="tab"
              aria-selected={aba === valor}
              onClick={() => setAba(valor)}
              className={`h-7 rounded-md px-2.5 text-sm font-semibold ${
                aba === valor
                  ? 'bg-ativo text-texto'
                  : 'text-texto-2 hover:text-texto'
              }`}
            >
              {valor === 'emoji' ? 'Emoji' : 'Figurinhas'}
            </button>
          ))}
        </div>
      )}
      {aba === 'figurinhas' && aoEscolherFigurinha ? (
        <Figurinhas canalId={canalId} aoEscolher={aoEscolherFigurinha} />
      ) : (
        <Emojis canalId={canalId} aoEscolher={aoEscolher} />
      )}
    </div>
  );
}

function Emojis({
  canalId,
  aoEscolher,
}: {
  canalId: string;
  aoEscolher: (emoji: EmojiEscolhido) => void;
}) {
  const cliente = useCliente();
  const guildId = useEstado(
    (estado) => estado.channels[canalId]?.guild_id ?? null,
  );
  const guildIds = useEstado((estado) => estado.guildIds);
  const guilds = useEstado((estado) => estado.guilds);
  const [unicode, setUnicode] = useState<EmojiUnicode[] | null>(null);
  const [busca, setBusca] = useState('');
  const [foco, setFoco] = useState<Item | null>(null);
  const rolagem = useRef<HTMLDivElement>(null);
  const campo = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let vivo = true;
    void carregarEmojis().then((lista) => vivo && setUnicode(lista));
    campo.current?.focus();
    return () => {
      vivo = false;
    };
  }, []);

  const externos = can(
    cliente.store.getState(),
    canalId,
    PermissionFlags.USE_EXTERNAL_EMOJIS,
  );
  const doServidor = useMemo(() => {
    const ordem = guildId
      ? [guildId, ...guildIds.filter((id) => id !== guildId)]
      : guildIds;
    return ordem
      .filter((id) => id === guildId || externos)
      .map((id) => guilds[id])
      .filter((guild) => guild?.emojis?.length)
      .map((guild) => ({
        nome: guild.name,
        emojis: (guild.emojis as APIEmoji[]).filter((emoji) => emoji.available),
      }));
  }, [guildId, guildIds, guilds, externos]);

  const secoes = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const proprios: Item[] = doServidor.flatMap((servidor) =>
      servidor.emojis.map((emoji) => ({
        emoji: { id: emoji.id, name: emoji.name, animated: emoji.animated },
        rotulo: `:${emoji.name}:`,
        grupo: servidor.nome,
      })),
    );
    const paraItem = (emoji: EmojiUnicode): Item => ({
      emoji: { id: null, name: emoji.unicode },
      rotulo: `:${emoji.atalhos[0] ?? emoji.nome}:`,
      grupo: GRUPOS_EMOJI[emoji.grupo] ?? '',
    });
    if (termo) {
      return [
        {
          titulo: 'Resultados',
          itens: [
            ...proprios.filter((item) =>
              item.emoji.name.toLowerCase().includes(termo),
            ),
            ...buscarEmojis(unicode ?? [], termo, 120).map(paraItem),
          ],
        },
      ];
    }
    const recentes = lerRecentes().map((emoji) => ({
      emoji,
      rotulo: emoji.id ? `:${emoji.name}:` : emoji.name,
      grupo: 'Usados com frequência',
    }));
    const lista: Array<{ titulo: string; itens: Item[] }> = [];
    if (recentes.length) {
      lista.push({ titulo: 'Usados com frequência', itens: recentes });
    }
    for (const servidor of doServidor) {
      lista.push({
        titulo: servidor.nome,
        itens: proprios.filter((item) => item.grupo === servidor.nome),
      });
    }
    const porGrupo = new Map<number, Item[]>();
    for (const emoji of unicode ?? []) {
      porGrupo.set(emoji.grupo, [
        ...(porGrupo.get(emoji.grupo) ?? []),
        paraItem(emoji),
      ]);
    }
    for (const [grupo, itens] of [...porGrupo.entries()].sort(
      (a, b) => a[0] - b[0],
    )) {
      lista.push({ titulo: GRUPOS_EMOJI[grupo] ?? 'Outros', itens });
    }
    return lista;
  }, [busca, doServidor, unicode]);

  const escolher = (item: Item) => {
    guardarRecente(item.emoji);
    aoEscolher(item.emoji);
  };

  return (
    <>
      <div className="shrink-0 border-b border-borda p-3">
        <label className="flex h-8 items-center gap-1.5 rounded-lg bg-lateral px-2.5 text-texto-3">
          <span className="sr-only">Buscar emoji</span>
          <input
            ref={campo}
            value={busca}
            onChange={(evento) => setBusca(evento.target.value)}
            onKeyDown={(evento) => {
              if (evento.key === 'Enter') {
                const primeiro = secoes[0]?.itens[0];
                if (primeiro) escolher(primeiro);
              }
            }}
            placeholder="Buscar emoji"
            className="h-[30px] min-w-0 grow border-0 bg-transparent text-sm text-texto outline-none placeholder:text-texto-3"
          />
          <Icone nome="busca" tamanho={18} />
        </label>
      </div>
      <div className="flex min-h-0 grow">
        <nav
          aria-label="Categorias de emoji"
          className="rolagem-escondida flex w-11 shrink-0 flex-col items-center gap-0.5 overflow-y-auto bg-chat py-1.5"
        >
          {secoes.map((secao, indice) => (
            <button
              key={secao.titulo}
              type="button"
              aria-label={secao.titulo}
              title={secao.titulo}
              onClick={() =>
                rolagem.current
                  ?.querySelector(`[data-secao="${indice}"]`)
                  ?.scrollIntoView({ block: 'start' })
              }
              className="flex size-8 shrink-0 items-center justify-center rounded-lg text-lg hover:bg-ativo"
            >
              <EmojiVisual emoji={secao.itens[0]?.emoji} tamanho={20} />
            </button>
          ))}
        </nav>
        <div
          ref={rolagem}
          className="rolagem min-w-0 grow overflow-y-auto px-[3px] pb-2"
        >
          {!unicode && !busca && (
            <p className="px-2 py-3 text-sm text-texto-3">Carregando emojis…</p>
          )}
          {secoes.map((secao, indice) => (
            <section
              key={secao.titulo}
              data-secao={indice}
              className="[content-visibility:auto]"
            >
              <h3 className="sticky top-0 z-[1] m-0 flex h-8 items-end bg-elevado px-[5px] pb-1.5 text-xs font-semibold tracking-[.04em] text-texto-3 uppercase">
                {secao.titulo}
              </h3>
              {secao.itens.length === 0 ? (
                <p className="px-2 py-2 text-sm text-texto-3">
                  Nada encontrado.
                </p>
              ) : (
                <div className="grid grid-cols-[repeat(auto-fill,40px)]">
                  {secao.itens.map((item) => (
                    <button
                      key={chaveDe(item.emoji)}
                      type="button"
                      aria-label={item.rotulo}
                      onClick={() => escolher(item)}
                      onMouseEnter={() => setFoco(item)}
                      onFocus={() => setFoco(item)}
                      className="flex size-10 items-center justify-center rounded-lg text-[26px] leading-none hover:bg-ativo"
                    >
                      <EmojiVisual emoji={item.emoji} tamanho={28} />
                    </button>
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      </div>
      <div className="flex h-12 shrink-0 items-center gap-2.5 border-t border-borda bg-chat px-3.5">
        {foco ? (
          <>
            <span aria-hidden="true" className="text-[28px] leading-none">
              <EmojiVisual emoji={foco.emoji} tamanho={28} />
            </span>
            <span className="truncate text-[15px] font-semibold">
              {foco.rotulo}
            </span>
            <span className="truncate text-sm text-texto-3">
              de {foco.grupo}
            </span>
          </>
        ) : (
          <span className="text-sm text-texto-3">Escolha um emoji</span>
        )}
      </div>
    </>
  );
}

/** Figurinhas do servidor (e dos outros, com "usar figurinhas externas"). */
function Figurinhas({
  canalId,
  aoEscolher,
}: {
  canalId: string;
  aoEscolher: (figurinha: APISticker) => void;
}) {
  const cliente = useCliente();
  const guildId = useEstado(
    (estado) => estado.channels[canalId]?.guild_id ?? null,
  );
  const guildIds = useEstado((estado) => estado.guildIds);
  const guilds = useEstado((estado) => estado.guilds);
  const externas = can(
    cliente.store.getState(),
    canalId,
    PermissionFlags.USE_EXTERNAL_STICKERS,
  );
  const [busca, setBusca] = useState('');
  const grupos = useMemo(() => {
    const ordem = guildId
      ? [guildId, ...guildIds.filter((id) => id !== guildId)]
      : guildIds;
    const termo = busca.trim().toLowerCase();
    return ordem
      .filter((id) => id === guildId || externas)
      .map((id) => guilds[id])
      .filter(Boolean)
      .map((guild) => ({
        nome: guild.name,
        figurinhas: (guild.stickers ?? []).filter(
          (figurinha) =>
            figurinha.available !== false &&
            (!termo ||
              figurinha.name.toLowerCase().includes(termo) ||
              (figurinha.tags ?? '').toLowerCase().includes(termo)),
        ),
      }))
      .filter((grupo) => grupo.figurinhas.length);
  }, [guildId, guildIds, guilds, externas, busca]);
  return (
    <>
      <div className="shrink-0 border-b border-borda p-3">
        <label className="flex h-8 items-center gap-1.5 rounded-lg bg-lateral px-2.5 text-texto-3">
          <span className="sr-only">Buscar figurinha</span>
          <input
            autoFocus
            value={busca}
            onChange={(evento) => setBusca(evento.target.value)}
            placeholder="Buscar figurinha"
            className="h-[30px] min-w-0 grow border-0 bg-transparent text-sm text-texto outline-none placeholder:text-texto-3"
          />
          <Icone nome="busca" tamanho={18} />
        </label>
      </div>
      <div className="rolagem min-h-0 grow overflow-y-auto p-2">
        {!grupos.length && (
          <p className="px-2 py-3 text-sm text-texto-3">
            {busca
              ? 'Nenhuma figurinha encontrada.'
              : 'Este servidor não tem figurinhas.'}
          </p>
        )}
        {grupos.map((grupo) => (
          <section key={grupo.nome}>
            <h3 className="m-0 px-1 pt-1 pb-2 text-xs font-semibold tracking-[.04em] text-texto-3 uppercase">
              {grupo.nome}
            </h3>
            <div className="grid grid-cols-4 gap-1">
              {grupo.figurinhas.map((figurinha) => (
                <button
                  key={figurinha.id}
                  type="button"
                  aria-label={`Enviar figurinha ${figurinha.name}`}
                  title={figurinha.name}
                  onClick={() => aoEscolher(figurinha)}
                  className="flex aspect-square items-center justify-center rounded-lg p-1 hover:bg-ativo"
                >
                  {figurinha.format_type === 3 ? (
                    <span className="text-center text-xs text-texto-3">
                      {figurinha.name}
                    </span>
                  ) : (
                    <img
                      src={noTamanho(stickerUrl(ARQUIVOS, figurinha), 96)}
                      alt=""
                      loading="lazy"
                      className="h-full w-full object-contain"
                    />
                  )}
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>
    </>
  );
}

export function EmojiVisual({
  emoji,
  tamanho,
}: {
  emoji?:
    | EmojiEscolhido
    | { id: string | null; name: string | null; animated?: boolean };
  tamanho: number;
}) {
  const animar = useAjusteDaConta('animate_emoji');
  if (!emoji) return null;
  if (emoji.id) {
    return (
      <img
        src={emojiUrl(ARQUIVOS, {
          id: emoji.id,
          animated: emoji.animated && animar,
        })}
        alt={`:${emoji.name}:`}
        draggable={false}
        loading="lazy"
        className="object-contain"
        style={{ width: tamanho, height: tamanho }}
      />
    );
  }
  return (
    <span style={{ fontSize: tamanho * 0.9, lineHeight: 1 }}>{emoji.name}</span>
  );
}
